import React from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import "./styles/App.css";
import { ThemeProvider, CssBaseline } from "@mui/material";
import { theme } from "./styles/Theme";
import { Standings } from "./components/Schedule/Standings";
import { Schedule } from "./components/Schedule/Schedule";
import { StatsRoot } from "./components/Stats/Home/StatsRoot";
import { initDatabase } from "./components/ClientDatabase/DataBaseService";
import { syncManager } from "./components/ClientDatabase/SyncManager";
import { Playbook } from "./components/Schemes/Playbook/PlaybookRoot";
import { DepthChartRoot } from "./components/Personnel/DepthChart/DepthChartRoot";
import "@fontsource/russo-one";
import "@fontsource/merriweather";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ClubProvider } from "./contexts/ClubContext";
import { SeasonProvider } from "./contexts/SeasonContext";
import { MainLayout } from "./components/Layout/MainLayout";
import { Login } from "./components/Auth/Login";
import { ForcePasswordChange } from "./components/Auth/ForcePasswordChange";
import { RosterProvider } from "./contexts/RosterContext";
import { ScheduleProvider } from "./contexts/ScheduleContext";
import { UserProvider } from "./contexts/UserContext";
import { DepthChartProvider } from "./contexts/DepthChartContext";
import { StatsProvider } from "./contexts/StatsContext";
import { SchemesProvider } from "./contexts/SchemesContext";
import { Recruits } from "./components/Personnel/Recruit/Recruits";
import { Roster } from "./components/Personnel/Roster/Roster";
import { GameLoggerRoot } from "./components/Stats/GameLogger/GamerLoggerRoot";
import { GameplanRoot } from "./components/Schemes/Gameplan/GameplanRoot";
import { OffenseSchemes } from "./components/Schemes/Offense/OffenseSchemes";
import { DefenseSchemes } from "./components/Schemes/Defense/DefenseSchemes";
import { SpecialTeamsSchemes } from "./components/Schemes/SpecialTeams/SpecialTeamsSchemes";
import { LoggerProvider } from "./contexts/LoggerContext";
import { LicenseInfo } from "@mui/x-license";
import { Profile } from "./components/Profile/profile";
import { Admin } from "./components/Profile/admin";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { GameStats } from "./components/Stats/Home/GameHistory/GameStats";
import { Messages } from "./components/Messages/Messages";
import AdminLayout from "./components/Layout/AdminLayout";
import { UsersAdminPage } from "./components/Admin/UsersAdminPage";
import { ClubsAdminPage } from "./components/Admin/ClubsAdminPage";

LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_KEY || "");

// Protected Route component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};

const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isAdmin, mustChangePassword } = useAuth();
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  if (mustChangePassword) {
    return <Navigate to="/change-password-required" replace />;
  }
  if (!isAdmin) {
    return <Navigate to="/schedule" replace />;
  }
  return <>{children}</>;
};

// Main App Content component
const AppContent = () => {
  const { isAuthenticated, mustChangePassword } = useAuth();

  // Offline Logger - Not used yet
  // const [dbInitialized, setDbInitialized] = React.useState(false);
  // const [dbError, setDbError] = React.useState<string | null>(null);

  // React.useEffect(() => {
  //   const initialize = async () => {
  //     try {
  //       const success = await initDatabase();
  //       setDbInitialized(success);
  //       if (success) {
  //         syncManager.startSync();
  //       }
  //     } catch (error) {
  //       console.error("Failed to initialize database:", error);
  //       setDbError(
  //         "Failed to initialize the database. Please refresh the page."
  //       );
  //     }
  //   };

  //   initialize();

  //   return () => {
  //     syncManager.cleanup();
  //   };
  // }, []);

  // if (dbError) {
  //   return <div className="error-message">{dbError}</div>;
  // }

  // if (!dbInitialized) {
  //   return <div className="loading">Initializing database...</div>;
  // }

  return (
    <Routes>
      <Route
        path="/"
        element={
          !isAuthenticated ? (
            <Login />
          ) : mustChangePassword ? (
            <Navigate to="/change-password-required" replace />
          ) : (
            <ProtectedRoute>
              <Schedule />
            </ProtectedRoute>
          )
        }
      />
      <Route
        path="/change-password-required"
        element={
          <ProtectedRoute>
            <ForcePasswordChange />
          </ProtectedRoute>
        }
      />
      <Route
        path="/standings"
        element={
          <ProtectedRoute>
            <Standings name="Virginia Independent Schools" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/schedule"
        element={
          <ProtectedRoute>
            <Schedule />
          </ProtectedRoute>
        }
      />
      <Route
        path="/recruits"
        element={
          <ProtectedRoute>
            <Recruits />
          </ProtectedRoute>
        }
      />
      {/* <Route
        path="/stats"
        element={
          <ProtectedRoute>
            <StatsRootTwo />
          </ProtectedRoute>
        }
      /> */}
      <Route
        path="/game-logger"
        element={
          <ProtectedRoute>
            <GameLoggerRoot />
          </ProtectedRoute>
        }
      />
      <Route
        path="/playbook"
        element={
          <ProtectedRoute>
            <Playbook />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages"
        element={
          <ProtectedRoute>
            <Messages />
          </ProtectedRoute>
        }
      />
      <Route
        path="/schemes/offense"
        element={
          <ProtectedRoute>
            <OffenseSchemes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/schemes/defense"
        element={
          <ProtectedRoute>
            <DefenseSchemes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/schemes/special-teams"
        element={
          <ProtectedRoute>
            <SpecialTeamsSchemes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/depth-chart"
        element={
          <ProtectedRoute>
            <DepthChartRoot />
          </ProtectedRoute>
        }
      />
      <Route
        path="/roster"
        element={
          <ProtectedRoute>
            <Roster />
          </ProtectedRoute>
        }
      />
      {/* In the future, the user may want to create gameplans each week */}
      {/* <Route
        path="/gameplan"
        element={
          <ProtectedRoute>
            <GameplanRoot />
          </ProtectedRoute>
        }
      /> */}
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <Admin />
          </AdminRoute>
        }
      />
      <Route
        path="/users"
        element={
          <AdminRoute>
            <UsersAdminPage />
          </AdminRoute>
        }
      />
      <Route
        path="/clubs"
        element={
          <AdminRoute>
            <ClubsAdminPage />
          </AdminRoute>
        }
      />
      <Route
        path="/game-stats/:gameLogId"
        element={
          <ProtectedRoute>
            <GameStats />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

// Create a new component to wrap the authenticated content
const AuthenticatedContent = () => {
  const { isAuthenticated, isAdmin } = useAuth();
  return (
    <div
      className="App"
      style={{ overflow: "hidden", height: "100vh", width: "100vw" }}
    >
      {isAuthenticated ? (
        isAdmin ? (
          <AdminLayout>
            <AppContent />
          </AdminLayout>
        ) : (
          <MainLayout>
            <AppContent />
          </MainLayout>
        )
      ) : (
        <AppContent />
      )}
    </div>
  );
};

function App() {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <AuthProvider>
            <UserProvider>
              <ClubProvider>
                <SeasonProvider>
                  <ScheduleProvider>
                    <DepthChartProvider>
                      <RosterProvider>
                        <SchemesProvider>
                          <StatsProvider>
                            <LoggerProvider>
                              <AuthenticatedContent />
                            </LoggerProvider>
                          </StatsProvider>
                        </SchemesProvider>
                      </RosterProvider>
                    </DepthChartProvider>
                  </ScheduleProvider>
                </SeasonProvider>
              </ClubProvider>
            </UserProvider>
          </AuthProvider>
        </Router>
      </ThemeProvider>
    </LocalizationProvider>
  );
}

export default App;
